import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api';
import theme from '../theme';
import { Card, Money, Spinner, Pagination, Empty, Button, Input } from '../components/common';

export default function AccountDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [account, setAccount] = useState(null);
  const [txns, setTxns] = useState(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  const loadTxns = useCallback((p) => {
    api.get(`/accounts/${id}/transactions?page=${p}&limit=20`)
      .then(setTxns).catch(console.error);
  }, [id]);

  useEffect(() => {
    Promise.all([
      api.get(`/accounts/${id}`),
      api.get(`/accounts/${id}/transactions?page=1&limit=20`),
    ]).then(([acc, t]) => {
      setAccount(acc);
      setTxns(t);
    }).catch(console.error).finally(() => setLoading(false));
  }, [id, loadTxns]);

  const handlePageChange = (p) => {
    setPage(p);
    loadTxns(p);
  };

  if (loading) return <div style={{ textAlign: 'center', padding: '60px' }}><Spinner size={32} /></div>;
  if (!account) return <div>Account not found</div>;

  const filteredTxns = txns?.data?.filter(t =>
    !search || t.reference?.toLowerCase().includes(search.toLowerCase()) ||
    t.transactionType?.toLowerCase().includes(search.toLowerCase())
  ) || [];

  return (
    <div>
      <Button variant="ghost" size="sm" onClick={() => navigate('/accounts')} style={{ marginBottom: '16px' }}>
        &larr; Back to accounts
      </Button>

      {/* Account header */}
      <Card style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <h1 style={{ margin: '0 0 4px', fontSize: '18px' }}>{account.name}</h1>
            <div style={{ fontSize: '14px', color: theme.colors.textLight, fontFamily: theme.fonts.mono }}>{account.accountNumber}</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '12px', color: theme.colors.textMuted }}>Available balance</div>
            <div style={{ fontSize: '28px', fontWeight: 700, color: theme.colors.primary }}>
              <Money amount={account.balance?.available || 0} currency={account.currency} />
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '24px', marginTop: '16px', flexWrap: 'wrap' }}>
          <div>
            <span style={{ fontSize: '12px', color: theme.colors.textMuted }}>Posted: </span>
            <Money amount={account.balance?.posted || 0} currency={account.currency} style={{ fontSize: '14px' }} />
          </div>
          <div>
            <span style={{ fontSize: '12px', color: theme.colors.textMuted }}>Pending: </span>
            <Money amount={account.balance?.pending || 0} currency={account.currency} style={{ fontSize: '14px' }} />
          </div>
          <div>
            <span style={{ fontSize: '12px', color: theme.colors.textMuted }}>Currency: </span>
            <span style={{ fontSize: '14px' }}>{account.currency}</span>
          </div>
        </div>
      </Card>

      {/* Transaction search */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '12px' }}>
        <h2 style={{ fontSize: '16px', margin: 0 }}>Transactions</h2>
        <Input
          placeholder="Search by reference..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ marginBottom: 0, width: '240px' }}
        />
      </div>

      {filteredTxns.length > 0 ? (
        <Card style={{ padding: 0, overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', minWidth: '600px' }}>
            <thead>
              <tr style={{ background: theme.colors.bgAlt }}>
                <th style={thStyle}>Date</th>
                <th style={thStyle}>Type</th>
                <th style={thStyle}>Reference</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Debit</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Credit</th>
              </tr>
            </thead>
            <tbody>
              {filteredTxns.map((t, i) => (
                <tr key={i}>
                  <td style={tdStyle}>{t.createdAt ? new Date(t.createdAt).toLocaleDateString('en-GB') : '-'}</td>
                  <td style={tdStyle}>{t.transactionType || t.type || '-'}</td>
                  <td style={tdStyle}>{t.reference || t.transactionReference || '-'}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', color: t.direction === 'DEBIT' ? theme.colors.danger : 'inherit' }}>
                    {t.direction === 'DEBIT' ? <Money amount={t.amount} currency={t.currency} /> : ''}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'right', color: t.direction === 'CREDIT' ? theme.colors.success : 'inherit' }}>
                    {t.direction === 'CREDIT' ? <Money amount={t.amount} currency={t.currency} /> : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination page={page} pages={txns?.pages || 1} onChange={handlePageChange} />
        </Card>
      ) : (
        <Empty message="No transactions found" />
      )}
    </div>
  );
}

const thStyle = { padding: '10px 16px', textAlign: 'left', fontWeight: 600, fontSize: '12px', color: theme.colors.textMuted, borderBottom: `1px solid ${theme.colors.border}` };
const tdStyle = { padding: '12px 16px', borderBottom: `1px solid ${theme.colors.borderLight}` };
